import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, MailOpen, CalendarCheck2, Heart, ShoppingBag, LogOut, Loader2, X, LifeBuoy, TrendingUp, Building2, RotateCcw, Save
} from "lucide-react";
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
    BarChart, Bar, PieChart, Pie, Cell, Legend
} from "recharts";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { useDocumentSeo } from "../lib/seo";

const TABS = [
    { id: "overview", label: "Overview", icon: <LayoutDashboard className="w-4 h-4" /> },
    { id: "intakes", label: "Veteran Intakes", icon: <LifeBuoy className="w-4 h-4" /> },
    { id: "contacts", label: "Inquiries", icon: <MailOpen className="w-4 h-4" /> },
    { id: "bookings", label: "Bookings", icon: <CalendarCheck2 className="w-4 h-4" /> },
    { id: "donations", label: "Donations", icon: <Heart className="w-4 h-4" /> },
    { id: "orders", label: "Orders", icon: <ShoppingBag className="w-4 h-4" /> },
    { id: "site", label: "Company Info", icon: <Building2 className="w-4 h-4" /> },
];

export default function AdminDashboard() {
    useDocumentSeo({ title: "Admin Console — TT Admins", noindex: true });
    const { user, loading, logout } = useAuth();
    const [tab, setTab] = useState("overview");
    const navigate = useNavigate();

    if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
    if (!user) return <Navigate to="/admin/login" replace />;

    const doLogout = async () => {
        await logout();
        navigate("/admin/login");
    };

    return (
        <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/logo-color.png" alt="" className="h-9 w-9 rounded-full" />
                        <div>
                            <div className="font-serif text-lg text-[var(--brand-navy)] leading-tight">Admin Console</div>
                            <div className="text-xs text-slate-500">Signed in as {user.email}</div>
                        </div>
                    </div>
                    <button onClick={doLogout} className="text-sm font-medium text-slate-600 hover:text-red-600 inline-flex items-center" data-testid="admin-logout-btn">
                        <LogOut className="w-4 h-4 mr-1.5" /> Logout
                    </button>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-12 gap-6">
                <nav className="lg:col-span-3 space-y-1" data-testid="admin-nav">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`w-full text-left px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-sm font-medium ${
                                tab === t.id
                                    ? "bg-[var(--brand-navy)] text-white"
                                    : "text-slate-700 hover:bg-slate-100"
                            }`}
                            data-testid={`admin-tab-${t.id}`}
                        >
                            {t.icon} {t.label}
                        </button>
                    ))}
                </nav>

                <section className="lg:col-span-9">
                    {tab === "overview" && <Overview />}
                    {tab === "intakes" && <Intakes />}
                    {tab === "contacts" && <DataList endpoint="/admin/contacts" testid="contacts-list" columns={["submitted_at", "name", "email", "service_interest", "message"]} />}
                    {tab === "bookings" && <DataList endpoint="/admin/bookings" testid="bookings-list" columns={["submitted_at", "name", "email", "service", "preferred_date", "preferred_time"]} />}
                    {tab === "donations" && <PaymentList endpoint="/admin/donations" type="donations" testid="donations-list" />}
                    {tab === "orders" && <PaymentList endpoint="/admin/orders" type="orders" testid="orders-list" />}
                    {tab === "site" && <SiteInfoEditor />}
                </section>
            </div>
        </div>
    );
}

function Overview() {
    const [stats, setStats] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [range, setRange] = useState(30);

    useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)); }, []);
    useEffect(() => {
        setAnalytics(null);
        api.get(`/admin/analytics?days=${range}`).then(({ data }) => setAnalytics(data));
    }, [range]);

    if (!stats) return <div className="text-slate-500"><Loader2 className="animate-spin inline mr-2" />Loading...</div>;
    const cards = [
        { label: "New Intakes", value: stats.intakes_new ?? 0, sub: `${stats.intakes ?? 0} lifetime`, accent: "bg-[var(--brand-coral)]" },
        { label: "Total Donations", value: `$${stats.donations_total.toFixed(2)}`, sub: `${stats.donations_count} gifts`, accent: "bg-[var(--brand-coral)]" },
        { label: "Total Orders", value: `$${stats.orders_total.toFixed(2)}`, sub: `${stats.orders_count} orders`, accent: "bg-[var(--brand-sky)]" },
        { label: "Inquiries", value: stats.contacts, sub: "lifetime", accent: "bg-[var(--brand-gold-deep)]" },
        { label: "Bookings", value: stats.bookings, sub: "lifetime", accent: "bg-[var(--brand-navy)]" },
    ];
    return (
        <div data-testid="overview">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="overview-cards">
                {cards.map((c) => (
                    <div key={c.label} className="bg-white rounded-2xl border border-slate-200 p-6">
                        <div className={`w-10 h-1 rounded-full ${c.accent} mb-4`} />
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{c.label}</div>
                        <div className="text-3xl font-serif text-[var(--brand-navy)] mt-2">{c.value}</div>
                        <div className="text-xs text-slate-500 mt-1">{c.sub}</div>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap gap-2 items-center justify-between mb-4">
                <div className="inline-flex items-center gap-2 text-[var(--brand-navy)]">
                    <TrendingUp className="w-4 h-4" />
                    <span className="font-serif text-xl">Analytics</span>
                </div>
                <div className="flex gap-1.5" data-testid="analytics-range">
                    {[7, 30, 90].map((d) => (
                        <button
                            key={d}
                            onClick={() => setRange(d)}
                            className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                                range === d
                                    ? "bg-[var(--brand-navy)] text-white border-[var(--brand-navy)]"
                                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                            }`}
                            data-testid={`range-${d}`}
                        >
                            Last {d}d
                        </button>
                    ))}
                </div>
            </div>

            {!analytics ? (
                <div className="bg-white rounded-2xl border border-slate-200 p-10 text-slate-500 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…
                </div>
            ) : (
                <AnalyticsPanels analytics={analytics} />
            )}
        </div>
    );
}

const REV_COLORS = { orders: "#2563EB", donations: "#E76F51" };
const PIE_COLORS = ["#F5B947", "#2563EB", "#E76F51", "#10B981", "#8B5CF6", "#0F172A", "#94A3B8"];

function AnalyticsPanels({ analytics }) {
    const revSeries = (analytics.revenue_series || []).map((r) => ({
        ...r,
        label: shortDay(r.date),
    }));
    const intakeSeries = (analytics.intake_series || []).map((r) => ({
        ...r,
        label: shortDay(r.date),
    }));
    const statusData = analytics.intake_status_breakdown || [];
    const topNeeds = analytics.top_needs || [];
    const topServices = analytics.top_services || [];
    const totals = analytics.totals || {};

    return (
        <div className="space-y-6" data-testid="analytics-panels">
            <div className="grid sm:grid-cols-4 gap-4">
                <MiniStat label="Range revenue" value={`$${(totals.revenue || 0).toFixed(2)}`} />
                <MiniStat label="Orders subtotal" value={`$${(totals.orders || 0).toFixed(2)}`} />
                <MiniStat label="Donations subtotal" value={`$${(totals.donations || 0).toFixed(2)}`} />
                <MiniStat label="Intakes in range" value={String(totals.intakes || 0)} />
            </div>

            <ChartCard title="Revenue — orders vs donations" testid="chart-revenue">
                <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={revSeries} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                        <defs>
                            <linearGradient id="revOrders" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={REV_COLORS.orders} stopOpacity={0.35} />
                                <stop offset="95%" stopColor={REV_COLORS.orders} stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="revDonations" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={REV_COLORS.donations} stopOpacity={0.35} />
                                <stop offset="95%" stopColor={REV_COLORS.donations} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="label" fontSize={11} stroke="#64748B" />
                        <YAxis fontSize={11} stroke="#64748B" tickFormatter={(v) => `$${v}`} />
                        <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                        <Legend iconType="circle" />
                        <Area type="monotone" dataKey="orders" name="Orders" stroke={REV_COLORS.orders} fill="url(#revOrders)" strokeWidth={2} />
                        <Area type="monotone" dataKey="donations" name="Donations" stroke={REV_COLORS.donations} fill="url(#revDonations)" strokeWidth={2} />
                    </AreaChart>
                </ResponsiveContainer>
            </ChartCard>

            <div className="grid lg:grid-cols-2 gap-6">
                <ChartCard title="Veteran intake submissions" testid="chart-intakes">
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={intakeSeries} margin={{ top: 10, right: 8, left: -14, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="label" fontSize={11} stroke="#64748B" />
                            <YAxis allowDecimals={false} fontSize={11} stroke="#64748B" />
                            <Tooltip />
                            <Bar dataKey="count" name="Intakes" fill="#E76F51" radius={[6, 6, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Intake pipeline" testid="chart-intake-status">
                    {statusData.length === 0 ? (
                        <EmptyChart label="No intake activity yet." />
                    ) : (
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie
                                    data={statusData}
                                    dataKey="count"
                                    nameKey="status"
                                    innerRadius={44}
                                    outerRadius={80}
                                    paddingAngle={2}
                                >
                                    {statusData.map((_, i) => (
                                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend iconType="circle" />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <ChartCard title="Most-requested urgent needs" testid="chart-top-needs">
                    {topNeeds.length === 0 ? (
                        <EmptyChart label="No urgent needs logged yet." />
                    ) : (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={topNeeds} layout="vertical" margin={{ top: 4, right: 12, left: 12, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis type="number" allowDecimals={false} fontSize={11} stroke="#64748B" />
                                <YAxis dataKey="need" type="category" width={130} fontSize={11} stroke="#64748B" />
                                <Tooltip />
                                <Bar dataKey="count" fill="#F5B947" radius={[0, 6, 6, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                <ChartCard title="Contact form — service interest" testid="chart-top-services">
                    {topServices.length === 0 ? (
                        <EmptyChart label="No inquiries yet." />
                    ) : (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={topServices} layout="vertical" margin={{ top: 4, right: 12, left: 12, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis type="number" allowDecimals={false} fontSize={11} stroke="#64748B" />
                                <YAxis dataKey="service" type="category" width={160} fontSize={11} stroke="#64748B" />
                                <Tooltip />
                                <Bar dataKey="count" fill="#2563EB" radius={[0, 6, 6, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
            </div>
        </div>
    );
}

function ChartCard({ title, testid, children }) {
    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">{title}</div>
            {children}
        </div>
    );
}

function MiniStat({ label, value }) {
    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
            <div className="text-xl font-serif text-[var(--brand-navy)] mt-1.5">{value}</div>
        </div>
    );
}

function EmptyChart({ label }) {
    return (
        <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">{label}</div>
    );
}

function shortDay(iso) {
    // "2026-02-14" -> "Feb 14"
    const d = new Date(iso + "T00:00:00");
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function DataList({ endpoint, columns, testid }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => { api.get(endpoint).then(({ data }) => { setItems(data); setLoading(false); }); }, [endpoint]);
    if (loading) return <Loader2 className="animate-spin" />;
    return (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={testid}>
            <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider text-left">
                    <tr>{columns.map((c) => <th key={c} className="px-5 py-3">{c.replace(/_/g, " ")}</th>)}</tr>
                </thead>
                <tbody>
                    {items.length === 0 ? (
                        <tr><td colSpan={columns.length} className="px-5 py-10 text-center text-slate-400">No records yet.</td></tr>
                    ) : items.map((row, i) => (
                        <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/50">
                            {columns.map((c) => (
                                <td key={c} className="px-5 py-3 text-slate-700 align-top">
                                    {c === "submitted_at" ? new Date(row[c]).toLocaleString() : (row[c] || "—")}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function PaymentList({ endpoint, type, testid }) {
    const [data, setData] = useState(null);
    useEffect(() => { api.get(endpoint).then((r) => setData(r.data)); }, [endpoint]);
    if (!data) return <Loader2 className="animate-spin" />;
    const list = type === "donations" ? data.donations : data.orders;
    return (
        <div data-testid={testid}>
            <div className="grid grid-cols-2 gap-4 mb-5">
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total</div>
                    <div className="text-3xl font-serif text-[var(--brand-navy)] mt-2">${data.total.toFixed(2)}</div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Count</div>
                    <div className="text-3xl font-serif text-[var(--brand-navy)] mt-2">{data.count}</div>
                </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider text-left">
                        <tr>
                            <th className="px-5 py-3">Date</th>
                            <th className="px-5 py-3">{type === "donations" ? "Donor" : "Items"}</th>
                            <th className="px-5 py-3">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.length === 0 ? (
                            <tr><td colSpan={3} className="px-5 py-10 text-center text-slate-400">No {type} yet.</td></tr>
                        ) : list.map((r, i) => (
                            <tr key={i} className="border-t border-slate-100">
                                <td className="px-5 py-3">{new Date(r.created_at).toLocaleString()}</td>
                                <td className="px-5 py-3 text-slate-700">{type === "donations" ? (r.donor_name || r.donor_email || "Anonymous") : (r.items || "—")}</td>
                                <td className="px-5 py-3 font-semibold">${r.amount.toFixed(2)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


const STATUS_COLORS = {
    new: "bg-amber-100 text-amber-800 border-amber-300",
    contacted: "bg-sky-100 text-sky-800 border-sky-300",
    enrolled: "bg-emerald-100 text-emerald-800 border-emerald-300",
    closed: "bg-slate-100 text-slate-600 border-slate-300",
};
const STATUS_OPTIONS = ["new", "contacted", "enrolled", "closed"];

function Intakes() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [active, setActive] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/intakes").then(({ data }) => { setItems(data); setLoading(false); });
    };
    useEffect(load, []);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/intakes/${id}`, { status });
            toast.success(`Marked as ${status}`);
            setItems((arr) => arr.map((it) => (it.id === id ? { ...it, status } : it)));
            if (active && active.id === id) setActive({ ...active, status });
        } catch { toast.error("Could not update status"); }
    };

    if (loading) return <Loader2 className="animate-spin" />;
    const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

    return (
        <div data-testid="admin-intakes">
            <div className="flex flex-wrap gap-2 mb-5">
                {["all", ...STATUS_OPTIONS].map((s) => {
                    const ct = s === "all" ? items.length : items.filter((i) => i.status === s).length;
                    return (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${
                                filter === s
                                    ? "bg-[var(--brand-navy)] text-white border-[var(--brand-navy)]"
                                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                            }`}
                            data-testid={`intake-filter-${s}`}
                        >
                            {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)} ({ct})
                        </button>
                    );
                })}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider text-left">
                        <tr>
                            <th className="px-5 py-3">When</th>
                            <th className="px-5 py-3">Name</th>
                            <th className="px-5 py-3">Phone</th>
                            <th className="px-5 py-3">Housing</th>
                            <th className="px-5 py-3">Needs</th>
                            <th className="px-5 py-3">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No intake requests yet.</td></tr>
                        ) : filtered.map((r) => (
                            <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50 cursor-pointer" onClick={() => setActive(r)} data-testid={`intake-row-${r.id}`}>
                                <td className="px-5 py-3 text-slate-600 text-xs whitespace-nowrap">{new Date(r.submitted_at).toLocaleString()}</td>
                                <td className="px-5 py-3 text-[var(--brand-navy)] font-medium">{r.name} <span className="text-xs text-slate-500">({r.relationship})</span></td>
                                <td className="px-5 py-3"><a href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()} className="text-[var(--brand-sky)] hover:underline">{r.phone}</a></td>
                                <td className="px-5 py-3 text-slate-700 text-xs">{r.housing_status}</td>
                                <td className="px-5 py-3 text-xs text-slate-700">{(r.urgent_needs || []).join(", ") || "—"}</td>
                                <td className="px-5 py-3">
                                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_COLORS[r.status] || STATUS_COLORS.new}`}>
                                        {r.status}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {active && (
                <div className="fixed inset-0 z-50 bg-[var(--brand-navy)]/70 flex items-center justify-center p-4" onClick={() => setActive(null)}>
                    <div className="bg-white rounded-2xl max-w-2xl w-full p-7 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="intake-detail-modal">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="font-serif text-2xl text-[var(--brand-navy)]">{active.name}</h3>
                                <div className="text-xs text-slate-500 mt-1">Submitted {new Date(active.submitted_at).toLocaleString()}</div>
                            </div>
                            <button onClick={() => setActive(null)}><X /></button>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-3 mb-5 text-sm">
                            <DetailRow label="Filled out by" value={active.relationship} />
                            <DetailRow label="Phone" value={<a href={`tel:${active.phone}`} className="text-[var(--brand-sky)] font-semibold">{active.phone}</a>} />
                            <DetailRow label="Email" value={active.email || "—"} />
                            <DetailRow label="Location" value={active.location || "—"} />
                            <DetailRow label="Branch" value={active.branch || "Not specified"} />
                            <DetailRow label="Housing" value={active.housing_status} />
                            <DetailRow label="Children" value={String(active.children_count || 0)} />
                            <DetailRow label="Urgent needs" value={(active.urgent_needs || []).join(", ") || "—"} />
                        </div>

                        {active.notes && (
                            <div className="mb-5">
                                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Notes from them</div>
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{active.notes}</div>
                            </div>
                        )}

                        <div className="border-t border-slate-100 pt-5">
                            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Update status</div>
                            <div className="flex flex-wrap gap-2">
                                {STATUS_OPTIONS.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setStatus(active.id, s)}
                                        disabled={active.status === s}
                                        className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${
                                            active.status === s
                                                ? `${STATUS_COLORS[s]} cursor-default`
                                                : "border-slate-200 bg-white hover:border-slate-400 text-slate-700"
                                        }`}
                                        data-testid={`intake-status-${s}`}
                                    >
                                        {s.charAt(0).toUpperCase() + s.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function DetailRow({ label, value }) {
    return (
        <div className="bg-slate-50 rounded-xl px-4 py-2.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
            <div className="text-sm text-[var(--brand-navy)] mt-0.5">{value}</div>
        </div>
    );
}


// ----------------------------------------------------------------------------
// Site / Company Info editor — lets the admin edit UEI, CAGE, NAICS, phone, etc.
// Backed by GET/PUT /api/admin/site/info (see backend server.py).
// ----------------------------------------------------------------------------
const SITE_TEXT_FIELDS = [
    { key: "company",  label: "Company Name",       placeholder: "Admin This! N Thatz Syncd, LLC" },
    { key: "tagline",  label: "Tagline",            placeholder: "Empowering communities…" },
    { key: "ceo",      label: "CEO / Founder",      placeholder: "Christina Slimp" },
    { key: "email",    label: "Contact Email",      placeholder: "cslimp@ttadmins.com" },
    { key: "phone",    label: "Phone",              placeholder: "(972) 942-9022" },
    { key: "address",  label: "Address",            placeholder: "1920 Lakepointe Dr, Lewisville, TX 75057" },
    { key: "uei",      label: "SAM.gov UEI",        placeholder: "MZYVNN8T4B71" },
    { key: "cage",     label: "CAGE Code",          placeholder: "0SQV5" },
];

function SiteInfoEditor() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(null);
    const [defaults, setDefaults] = useState(null);
    const [overridden, setOverridden] = useState([]);
    const [naicsText, setNaicsText] = useState("");
    const [certsText, setCertsText] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/site/info");
            setForm(data.current);
            setDefaults(data.defaults);
            setOverridden(data.overridden_keys || []);
            setNaicsText((data.current.naics || []).join(", "));
            setCertsText((data.current.certifications || []).join(", "));
        } catch {
            toast.error("Could not load company info");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const parseList = (text) => text.split(",").map((s) => s.trim()).filter(Boolean);

    const onSave = async () => {
        setSaving(true);
        try {
            const payload = {};
            SITE_TEXT_FIELDS.forEach((f) => {
                if (typeof form[f.key] === "string") payload[f.key] = form[f.key].trim();
            });
            payload.naics = parseList(naicsText);
            payload.certifications = parseList(certsText);
            await api.put("/admin/site/info", payload);
            toast.success("Company info saved — public site now reflects your changes");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const onResetAll = async () => {
        if (!window.confirm("Reset ALL company info fields back to their factory defaults?")) return;
        try {
            await api.post("/admin/site/info/reset", {});
            toast.success("All fields reset to defaults");
            await load();
        } catch {
            toast.error("Could not reset");
        }
    };

    const onResetField = async (key) => {
        try {
            await api.post("/admin/site/info/reset", { keys: [key] });
            toast.success(`${key} reset to default`);
            await load();
        } catch {
            toast.error("Could not reset field");
        }
    };

    if (loading || !form) {
        return <div className="text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading company info…</div>;
    }

    const isOverridden = (key) => overridden.includes(key);

    return (
        <div className="space-y-6" data-testid="admin-site-info">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-start justify-between mb-2 gap-4 flex-wrap">
                    <div>
                        <h2 className="font-serif text-2xl text-[var(--brand-navy)] flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-[var(--brand-coral)]" />
                            Company Info
                        </h2>
                        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                            Edits here update the public <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">/api/site/info</code> feed —
                            the site header, footer badge, About page and JSON-LD schemas all read from it. No code deploy needed.
                        </p>
                    </div>
                    <button
                        onClick={onResetAll}
                        className="text-xs font-semibold text-slate-500 hover:text-red-600 inline-flex items-center gap-1.5"
                        data-testid="site-info-reset-all-btn"
                    >
                        <RotateCcw className="w-3.5 h-3.5" /> Reset all to defaults
                    </button>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mt-5">
                    {SITE_TEXT_FIELDS.map((f) => (
                        <SiteField
                            key={f.key}
                            field={f}
                            value={form[f.key] || ""}
                            defaultValue={defaults?.[f.key]}
                            overridden={isOverridden(f.key)}
                            onChange={(v) => setField(f.key, v)}
                            onReset={() => onResetField(f.key)}
                        />
                    ))}
                </div>

                <div className="mt-6 space-y-5">
                    <ListField
                        label="NAICS Codes"
                        helper="Comma-separated 6-digit codes. Order shown on the About page NAICS grid."
                        testid="site-naics-input"
                        value={naicsText}
                        onChange={setNaicsText}
                        overridden={isOverridden("naics")}
                        onReset={() => onResetField("naics")}
                        defaults={(defaults?.naics || []).join(", ")}
                    />
                    <ListField
                        label="Certifications"
                        helper="Comma-separated. Shown in the header, footer, About page, and JSON-LD."
                        testid="site-certs-input"
                        value={certsText}
                        onChange={setCertsText}
                        overridden={isOverridden("certifications")}
                        onReset={() => onResetField("certifications")}
                        defaults={(defaults?.certifications || []).join(", ")}
                    />
                </div>

                <div className="mt-8 flex items-center gap-3">
                    <button
                        onClick={onSave}
                        disabled={saving}
                        className="btn-gold !py-2.5 !px-6 text-sm disabled:opacity-60 disabled:cursor-wait"
                        data-testid="site-info-save-btn"
                    >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        {saving ? "Saving…" : "Save Changes"}
                    </button>
                    <span className="text-xs text-slate-500">
                        {overridden.length === 0
                            ? "No overrides yet — everything is at factory defaults."
                            : `${overridden.length} field${overridden.length === 1 ? "" : "s"} currently overridden.`}
                    </span>
                </div>
            </div>
        </div>
    );
}

function SiteField({ field, value, defaultValue, overridden, onChange, onReset }) {
    return (
        <label className="block" data-testid={`site-field-${field.key}`}>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    {field.label}
                    {overridden && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-bold tracking-wider normal-case">
                            OVERRIDDEN
                        </span>
                    )}
                </span>
                {overridden && (
                    <button
                        type="button"
                        onClick={onReset}
                        className="text-[10px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1"
                        data-testid={`site-field-reset-${field.key}`}
                    >
                        <RotateCcw className="w-3 h-3" /> reset
                    </button>
                )}
            </div>
            <input
                type="text"
                value={value}
                placeholder={field.placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--brand-gold)] text-sm"
                data-testid={`site-input-${field.key}`}
            />
            {defaultValue !== undefined && defaultValue !== value && (
                <div className="text-[10px] text-slate-400 mt-1 truncate">
                    Default: <span className="font-mono">{String(defaultValue)}</span>
                </div>
            )}
        </label>
    );
}

function ListField({ label, helper, testid, value, onChange, overridden, onReset, defaults }) {
    return (
        <label className="block">
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    {label}
                    {overridden && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-bold tracking-wider normal-case">
                            OVERRIDDEN
                        </span>
                    )}
                </span>
                {overridden && (
                    <button
                        type="button"
                        onClick={onReset}
                        className="text-[10px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1"
                    >
                        <RotateCcw className="w-3 h-3" /> reset
                    </button>
                )}
            </div>
            <textarea
                rows={2}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--brand-gold)] text-sm font-mono"
                data-testid={testid}
            />
            <div className="text-[10px] text-slate-400 mt-1">{helper}</div>
            {defaults && defaults !== value && (
                <div className="text-[10px] text-slate-400 mt-0.5 truncate">
                    Default: <span className="font-mono">{defaults}</span>
                </div>
            )}
        </label>
    );
}
